/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Skeleton } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  getCandidatePosition,
  getCandidateName,
  type PositionNameResolver,
} from '@sportninja/common/features/playerFinder/candidateDisplay'
// Penalty chart hidden until the BE ships penalty history — its data is mock
// (USE_MOCK_PENALTIES). Restore this line and the `penalties` prop below to
// bring it back; the total minutes are real and stay.
// import { resolvePenalties } from '@sportninja/common/features/playerFinder/penalties'
import { getSkillLevelColor } from '@sportninja/common/features/playerFinder/skillLevelColor'
import type { SpareCandidate } from '@sportninja/common/features/playerFinder/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  resolveAssetUrl,
  type ImageLike,
} from '@sportninja/common/utils/assetUrl'
import React from 'react'
import BaseModal from 'src/components/ts-components/BaseModal'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { font, weightBold, weightRegular } from 'src/components/css'
import { usePlayerProfile } from 'src/features/profile/hooks/usePlayerProfile'
import { usePenaltyMinutes } from '../hooks/usePenaltyMinutes'
import { formatRate } from '../utils/candidateFormat'
import CandidateAvailableDays from './CandidateAvailableDays'
import CandidateAvailableTimes from './CandidateAvailableTimes'
import PenaltySection from './PenaltySection'
import PlayerBioSection from './PlayerBioSection'
import PlayerFinderStatsOverview from './PlayerFinderStatsOverview'
import PlayerInfoRow from './PlayerInfoRow'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

export interface SpareCandidateDetailModalProps {
  candidate: SpareCandidate | null
  isOpen: boolean
  sending: boolean
  resolvePositionName?: PositionNameResolver
  sportId?: string
  onSend: () => void
  onClose: () => void
}

/**
 * Candidate detail view opened from a spare-candidate card: header, info row,
 * available days, bio, penalty section, career stats and a single-player
 * request button. The request deliberately ignores the list's checkbox
 * selection — it asks only for the open player, matching mobile.
 */
const SpareCandidateDetailModal: React.FC<SpareCandidateDetailModalProps> = ({
  candidate,
  isOpen,
  sending,
  resolvePositionName,
  sportId,
  onSend,
  onClose,
}) => {
  'use memo'
  // Full player (age, weight, height, teams) drives the info row + season stats.
  // Enabled only while a candidate is open.
  const { data: player, loading: playerLoading } = usePlayerProfile(
    isOpen ? candidate?.id : undefined
  )
  const penaltyMinutes = usePenaltyMinutes(player, sportId)

  if (!isOpen || !candidate) {
    return null
  }

  const avatarUrl =
    resolveAssetUrl(candidate.image as ImageLike) ?? PLAYER_PLACEHOLDER
  const position = getCandidatePosition(candidate, resolvePositionName)
  // Real API value. A player who hasn't set a rate comes back null/'' — the
  // rate pill is then omitted rather than showing a made-up figure.
  const chargeRate =
    candidate.charge_rate != null && candidate.charge_rate !== ''
      ? candidate.charge_rate
      : undefined
  const hasRate = chargeRate !== undefined

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      // Level 2: opened from within the (level-1) Player Finder, so its mask
      // dims the finder instead of sitting at the same z-index.
      elevated={2}
      desktopWidth='640px'
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords('Player Information')}
        </Text>
      }
      actions={
        <div css={actionsStyle}>
          <Button
            text='Request Player to Play'
            variant='primary'
            // Hug the text instead of filling the footer, matching the finder's
            // own footer button and the attendance modal's.
            style={'flex: 0 0 auto; width: auto;'}
            isLoading={sending}
            onClick={onSend}
          />
        </div>
      }
    >
      <div css={bodyStyle}>
        <div css={headerStyle}>
          <img css={avatarStyle} src={avatarUrl} alt='' />
          <div css={headerInfoStyle}>
            <span css={nameStyle}>{getCandidateName(candidate)}</span>
            <PlayerInfoRow
              age={player?.age}
              weight={player?.weight}
              height={player?.height}
              skillLevel={candidate.skill_level}
              skillColor={getSkillLevelColor(candidate.skill_level)}
              gender={candidate.gender}
            />
          </div>

          {/* Position sits close to the name it describes… */}
          {!!position && (
            <div css={[metaItemStyle, positionMetaStyle]}>
              <span css={metaTitleStyle}>{capitalizeWords('Position')}</span>
              <span css={metaValueStyle}>{position}</span>
            </div>
          )}

          {/* …while Rate is pinned to the far right, separated from it. */}
          {hasRate && (
            <div css={[metaItemStyle, rateMetaStyle]}>
              <span css={metaTitleStyle}>{capitalizeWords('Rate')}</span>
              <span css={rateValueStyle}>${formatRate(chargeRate)}</span>
              <span css={rateSuffixStyle}>per game</span>
            </div>
          )}
        </div>

        <CandidateAvailableDays availableDays={candidate.available_days} />
        <CandidateAvailableTimes availableTimes={candidate.available_times} />
        <PlayerBioSection bio={candidate.bio} />
        <PenaltySection
          penaltyMinutes={penaltyMinutes}
          // penalties={resolvePenalties()}
        />

        {playerLoading && !player ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : player ? (
          <PlayerFinderStatsOverview player={player} sportId={sportId} />
        ) : null}
      </div>
    </BaseModal>
  )
}

export default SpareCandidateDetailModal

/* The scroll container is the body itself, so its scrollbar is drawn INSIDE
   these bounds — without the right padding it overlaps the header's Rate
   column and the section panels. */
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 12px;
`

/* Top-aligned: the Position/Rate columns are taller than the avatar, so
   centring would drift the name away from the top of the card. The gap also
   separates those columns from the name block. */
const headerStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`

const avatarStyle = css`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  background-color: ${colors.SOFT_STEEL};
  flex: 0 0 auto;
`

/* Takes the header's spare width so the fixed Position/Rate columns after it
   sit at a stable x-position for every player. */
const headerInfoStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
`

/* `font.heading`, not `font.title` — the title face forces uppercase (Rift on
   SportNinja, Neue Plak Condensed + text-transform on Canlan), which shouted
   the player's name. The heading face is the mixed-case condensed variant. */
const nameStyle = css`
  ${font.heading}
  ${weightBold}
  font-size: 30px;
  line-height: 34px;
  color: ${colors.WHITE};
`

/* Position and Rate are fixed-width columns at the end of the header row. The
   name block (flex: 1) absorbs the slack, so both columns land at a consistent
   x-position regardless of how long the player's name is, and Rate no longer
   collides with the body's scrollbar. */
const positionMetaStyle = css`
  flex: 0 0 120px;
`

const rateMetaStyle = css`
  flex: 0 0 96px;
`

const metaItemStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const metaTitleStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 14px;
  line-height: 18px;
  color: ${colors.NEUTRAL_50};
`

const metaValueStyle = css`
  ${font.body}
  ${weightRegular}
  font-size: 14px;
  line-height: 18px;
  color: ${colors.WHITE};
`

const rateValueStyle = css`
  ${font.title}
  ${weightBold}
  font-size: 20px;
  line-height: 20px;
  letter-spacing: 1px;
  color: ${colors.SUCCESS_200};
`

const rateSuffixStyle = css`
  ${font.body}
  ${weightRegular}
  font-size: 11px;
  line-height: 14px;
  color: ${colors.NEUTRAL_50};
`

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
`
